import { PremiacaoIsolada, Combo, ExperiencePurchaseResponse } from 'types/experienceCatalog';
import { api } from './api';
import { IRegulation } from './regulation.service';

export async function fetchExperienceCatalogList(playerId: string, ciclo: string) {
  try {
    const payload = { player: playerId, _id: `experiencias_${playerId}_ciclo_${ciclo}`, ciclo: ciclo };
    const result = await api.put(`database/catalogo_experiencias__c`, payload);
    return result.data;
  } catch (e) {
    throw e;
  }
}

export async function getDeadLineExperienceCatalog(cycle: string) {
  try {
    const result = await api.get(`database/periodo_catalogo__c?strict=true&q=cycle:'${cycle}'`);
    return result.data[0];
  } catch (e) {
    throw e;
  }
}

/*
 * Espera máxima pelo resgate. Passado esse tempo o axios cancela a chamada e lança exceção, e o
 * modal mostra "Não recebemos a confirmação" (RES-23).
 *
 * O cancelamento é só do navegador: o servidor pode gravar o resgate depois. Por isso a página
 * confere o resgate do ciclo antes de oferecer nova tentativa, e a trigger recusa a duplicata.
 */
export const PURCHASE_TIMEOUT_MS = 60_000;

export async function purchaseItemExperienceCatalog(
  item: PremiacaoIsolada | Combo,
  playerId: string,
): Promise<ExperiencePurchaseResponse> {
  try {
    const payload = {
      item: item.itemIdPurchase,
      player: playerId,
      extra: item,
    };
    const result = await api.post(`virtualgoods/purchase`, payload, { timeout: PURCHASE_TIMEOUT_MS });
    return result.data;
  } catch (e) {
    throw e;
  }
}

export async function fetchQuestionExperienceCatalog() {
  try {
    const result = await api.get(`database/feedback_questions__c`);
    return result.data;
  } catch (e) {
    throw e;
  }
}

export async function VerifyExperienceCatalogItens(playerId: string) {
  try {
    const result = await api.post(`find/verify_experience_by_catalog`, { player: playerId });
    return result.data[0];
  } catch (e) {
    throw e;
  }
}

export async function loadExperiencePurchase(playerId: string, cicle: string) {
  try {
    const payload = { playerId: playerId, cicleId: cicle };

    const result = await api.post(`find/resgate_experiencias`, payload);

    if (result.data && result.data.length > 0) {
      return result.data[0];
    }

    return null;
  } catch (e) {
    throw e;
  }
}

const UNDO_GENERIC_ERROR = 'Não foi possível cancelar o resgate. Tente novamente.';

// Cancela o resgate do ciclo. A regra mora inteira na trigger `before_create` de
// `desfazer_resgate__c`: o corpo leva SÓ o ciclo, porque o jogador vem do token — nunca do
// corpo do POST. Prazo, existência do período e resgate ativo são validados no servidor.
//
// Recusa chega como HTTP 500 com a mensagem do `throw` no corpo, prefixada pela classe Java
// (`java.lang.RuntimeException: ...`). Descascar isso é responsabilidade daqui, para o
// chamador ter uma única string pronta para exibir.
export async function undoExperiencePurchase(ciclo: string) {
  try {
    const result = await api.post(`database/desfazer_resgate__c`, { ciclo });
    return result.data;
  } catch (e: any) {
    const message = e?.response?.data?.message;

    if (typeof message !== 'string' || !message.trim()) {
      throw new Error(UNDO_GENERIC_ERROR);
    }

    throw new Error(message.replace(/^[\w.$]+(Exception|Error):\s*/, '').trim() || UNDO_GENERIC_ERROR);
  }
}

/** Um item do Catálogo que estava esgotado e voltou a ter vaga por cancelamento.
 *  Payload do aggregate `catalogo_itens_liberados` — o `nome` vem AO VIVO do
 *  `catalog_item` (ainda com o prefixo de catalogação; quem exibe o remove). */
export interface CatalogRestockItem {
  item: string;
  nome: string;
  time: number;
  vagas: number;
}

// Itens de volta ao estoque, já filtrados pelo backend para o jogador do TOKEN:
// elegibilidade (perfil, saldo, faixa, sobra estrita), marca-d'água de visto e
// estoque vivo. O corpo vai vazio de propósito — enviar playerId não teria efeito
// (o prepare() usa o token) e sugeriria um controle que o cliente não tem.
//
// NUNCA lança: a notificação é ornamental, e o Dashboard não pode quebrar nem
// esperar por ela. Qualquer falha (rede, 5xx, payload fora do formato) devolve
// lista vazia — a modal simplesmente não abre nesta visita.
export async function getCatalogRestock(): Promise<CatalogRestockItem[]> {
  try {
    const { data } = await api.post(`find/catalogo_itens_liberados`, {});

    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter(
      (d: any): d is CatalogRestockItem =>
        !!d && typeof d.item === 'string' && typeof d.nome === 'string' && typeof d.time === 'number',
    );
  } catch {
    return [];
  }
}

// Grava a marca-d'água do "visto" (R5): eventos com `time` até este valor não
// voltam a aparecer para o jogador. O PUT substitui o documento inteiro — e o
// documento É só isto, então não há campo alheio a preservar. A trigger
// `CatVist1` (before_update) força o `_id` para o jogador do token quando a
// chamada vem com claim, então mesmo um `playerId` errado aqui não escreve no
// documento de outra pessoa.
//
// NUNCA lança: se a gravação falhar, a modal volta na próxima visita — falha
// benigna, preferível a quebrar o fechamento.
export async function markCatalogRestockSeen(playerId: string, time: number): Promise<void> {
  try {
    await api.put(`database/catalogo_estoque_liberado_visto__c`, {
      _id: playerId,
      time,
      updated: Date.now(),
    });
  } catch {
    // engolido de propósito — ver comentário acima
  }
}

export async function getRegulationExperienceCatalog(ciclo: string) {
  const { data } = await api.get<IRegulation[]>(`database/regulamento_experiencias_livelo__c?q=ciclo:"${ciclo}"`, {
    headers: {
      Authorization: 'Basic NjMyYjU2YzE1NDVkZDA3Njc2NjIxNzA5OjYzNGQyN2QwOTI3MWYzMzdiMzlmMTQ1OQ==',
    },
  });
  if (Array.isArray(data)) {
    return data[0] as any;
  } else {
    return null;
  }
}

export const registerFeedbackExperienceCatalog: any = (
  userId: string,
  message: string,
  questions: any[],
  permitidoIdentificar: boolean,
  cycle: string,
) => {
  return new Promise((resolve, reject) => {
    const questionsList = Object.assign({}, questions);

    const payload = {
      userId,
      message,
      time: { $date: new Date().getTime() },
      questions: questionsList,
      permitidoIdentificar,
      cycle,
    };

    api
      .post(`database/feedback_experience_catalog__c`, payload)
      .then(response => {
        resolve(response.data);
      })
      .catch(error => {
        reject(error);
      });
  });
};

export const fetchFeedbackExperienceCatalog: any = async (
  userId: string,
  cycle: string,
) => {
  const { data } = await api.post(`find/verify_feedback_catalog`, { cycle });
  if (Array.isArray(data)) {
    return data;
  } else {
    return null;
  }
};
